import type { GraphQLResult } from 'aws-amplify/api'
import { useQuery } from '@tanstack/react-query'
import { useLocale } from 'next-intl'

import { getAmplifyClient } from '@/services/amplifyClient'
import { sectionAdapter, sortOptionsByLabel } from '@/services/adapters'
import { getSectionQuery, getSectionsQuery } from '@/services/graphql'
import { selectPrimaryProjectBlueprint } from '@/services/queries/blueprint'
import { Blueprint, Language, SectionSchema } from '@/types'

const client = getAmplifyClient()

export const useSection = (id?: string) => {
  return useQuery({
    queryKey: ['section', id],
    queryFn: () => getSection(id),
    enabled: !!id,
  })
}

export const getSection = async (id?: string) => {
  const result = (await client.graphql({
    query: getSectionQuery,
    variables: { id },
  })) as GraphQLResult<{ getSection: SectionSchema }>
  return result.data?.getSection
}

export const useSections = (projectId: string | undefined) => {
  const locale = useLocale()
  const lang = locale.toUpperCase() as Language

  return useQuery({
    queryKey: ['sections', projectId],
    queryFn: () => getSections(projectId),
    enabled: !!projectId,
    select: (data) => {
      if (!data) return []

      return data
        .map((section) => sectionAdapter(section, lang))
        .sort(sortOptionsByLabel)
    },
  })
}

const getSections = async (id?: string) => {
  const result = (await client.graphql({
    query: getSectionsQuery,
    variables: { id },
  })) as GraphQLResult<{ blueprintsByProject: { items: Blueprint[] } }>
  return (
    selectPrimaryProjectBlueprint(result.data?.blueprintsByProject?.items)
      ?.sections?.items ?? []
  )
}
